import {
  WifiOff as WifiOffIcon,
  Wifi as WifiIcon,
  CloudOff as CloudOffIcon,
} from '@mui/icons-material';
import {
  Snackbar,
  Alert,
  Box,
  Typography,
  Slide,
  Chip,
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';

function SlideTransition(props) {
  return <Slide {...props} direction="down" />;
}

function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(true);
  const [showOfflineMessage, setShowOfflineMessage] = useState(false);
  const [showOnlineMessage, setShowOnlineMessage] = useState(false);
  const consecutiveFailures = useRef(0);

  useEffect(() => {
    // API 성공 이벤트 핸들러 (실제 사용자 요청 성공 시)
    const handleApiSuccess = () => {
      consecutiveFailures.current = 0;
      if (!isOnline) {
        setIsOnline(true);
        setShowOfflineMessage(false);
        setShowOnlineMessage(true);
        setTimeout(() => setShowOnlineMessage(false), 3000);
      }
    };

    // API 네트워크 오류 이벤트 핸들러 (실제 사용자 요청 실패 시)
    const handleApiNetworkError = () => {
      consecutiveFailures.current += 1;

      // 연속 3회 실패 시에만 오프라인 판정 (false positive 방지)
      if (consecutiveFailures.current >= 3 && isOnline) {
        setIsOnline(false);
        setShowOnlineMessage(false);
        setShowOfflineMessage(true);
      }
    };

    // 브라우저 온라인 이벤트 (보조 메커니즘)
    const handleBrowserOnline = () => {
      consecutiveFailures.current = 0;
      if (!isOnline) {
        setIsOnline(true);
        setShowOfflineMessage(false);
        setShowOnlineMessage(true);
        setTimeout(() => setShowOnlineMessage(false), 3000);
      }
    };

    // 브라우저 오프라인 이벤트 (보조 메커니즘)
    const handleBrowserOffline = () => {
      consecutiveFailures.current = 3; // 즉시 오프라인 판정
      setIsOnline(false);
      setShowOnlineMessage(false);
      setShowOfflineMessage(true);
    };

    // 커스텀 이벤트 리스너 등록 (Axios Interceptor에서 발생)
    window.addEventListener('api-success', handleApiSuccess);
    window.addEventListener('api-network-error', handleApiNetworkError);

    // 브라우저 네이티브 이벤트 리스너 등록
    window.addEventListener('online', handleBrowserOnline);
    window.addEventListener('offline', handleBrowserOffline);

    return () => {
      window.removeEventListener('api-success', handleApiSuccess);
      window.removeEventListener('api-network-error', handleApiNetworkError);
      window.removeEventListener('online', handleBrowserOnline);
      window.removeEventListener('offline', handleBrowserOffline);
    };
  }, [isOnline]);

  return (
    <>
      {/* 오프라인 상태 표시 - 상단 고정 */}
      {!isOnline && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bgcolor: 'error.main',
            color: 'error.contrastText',
            zIndex: 9999,
            py: 1,
            px: 2,
            textAlign: 'center',
            boxShadow: 2,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            <WifiOffIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              인터넷 연결이 끊어졌습니다
            </Typography>
            <Chip 
              size="small" 
              label="오프라인" 
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'inherit',
                fontWeight: 600,
              }} 
            />
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
            일부 기능이 제한될 수 있습니다. 연결을 확인해 주세요.
          </Typography>
        </Box>
      )}

      {/* 오프라인 알림 스낵바 */}
      <Snackbar
        open={showOfflineMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={SlideTransition}
      >
        <Alert 
          severity="error" 
          icon={<CloudOffIcon />}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            오프라인 상태입니다
          </Typography>
          <Typography variant="caption">
            데이터 동기화가 중단되었습니다
          </Typography>
        </Alert>
      </Snackbar>

      {/* 온라인 복구 알림 스낵바 */}
      <Snackbar
        open={showOnlineMessage}
        autoHideDuration={3000}
        onClose={() => setShowOnlineMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={SlideTransition}
      >
        <Alert 
          severity="success" 
          icon={<WifiIcon />}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            다시 온라인 상태입니다
          </Typography>
          <Typography variant="caption">
            데이터 동기화가 재개됩니다
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
}

export default OfflineIndicator;